// =============================================================================
// components/notifications/R2NotificationCenter.tsx
// Global Notification Bell — lives in TopBar; full-featured notification hub
// Categories · Filter · Search · Mark Read · Mark All Read · Delete
// =============================================================================
import { useState, useEffect, useRef, useCallback, type JSX } from 'react';
import {
  Bell, X, Check, CheckCheck, ShoppingCart, FileText, ClipboardCheck,
  MessageSquare, Bot, AlertTriangle, Clock, DollarSign, Package,
  Trash2, ExternalLink, Loader2, Search, Filter, Inbox,
  CreditCard, Settings, Users, Shield,
} from 'lucide-react';
import type { Route } from '../../App.js';

const API_URL = (import.meta as any).env?.VITE_BACKEND_URL
  ?? (typeof process !== 'undefined' ? (process.env as any)?.REACT_APP_BACKEND_URL ?? '' : '');

const C = {
  bg:     'rgba(2,6,18,0.98)',
  panel:  'rgba(6,12,28,0.96)',
  card:   'rgba(8,16,36,0.88)',
  border: 'rgba(30,50,90,0.4)',
  teal:   '#5eead4',
  amber:  '#fbbf24',
  blue:   '#60a5fa',
  green:  '#4ade80',
  red:    '#f87171',
  purple: '#a78bfa',
  text:   '#f0f4ff',
  muted:  'rgba(148,172,210,0.65)',
  dim:    'rgba(80,110,150,0.3)',
};

const CATEGORIES: { key: string; label: string; icon: JSX.Element; color: string }[] = [
  { key: 'all',           label: 'All',          icon: <Bell size={11}/>,           color: C.teal   },
  { key: 'marketplace',   label: 'Orders',        icon: <ShoppingCart size={11}/>,   color: C.amber  },
  { key: 'ai',            label: 'AI Jobs',       icon: <Bot size={11}/>,            color: C.purple },
  { key: 'permits',       label: 'Permits',       icon: <FileText size={11}/>,       color: C.blue   },
  { key: 'tasks',         label: 'Tasks',         icon: <ClipboardCheck size={11}/>, color: C.green  },
  { key: 'billing',       label: 'Billing',       icon: <CreditCard size={11}/>,     color: C.amber  },
  { key: 'rfis',          label: 'RFIs',          icon: <MessageSquare size={11}/>,  color: C.muted  },
  { key: 'system',        label: 'System',        icon: <Settings size={11}/>,       color: C.dim    },
];

const PRIORITY_COLOR: Record<string, string> = {
  urgent: C.red, high: C.amber, normal: C.blue, low: C.dim,
};

const CATEGORY_ICON: Record<string, JSX.Element> = {
  marketplace:   <ShoppingCart size={12}/>,
  permits:       <FileText size={12}/>,
  tasks:         <ClipboardCheck size={12}/>,
  rfis:          <MessageSquare size={12}/>,
  ai:            <Bot size={12}/>,
  meetings:      <Clock size={12}/>,
  documents:     <FileText size={12}/>,
  commissioning: <ClipboardCheck size={12}/>,
  projects:      <AlertTriangle size={12}/>,
  billing:       <DollarSign size={12}/>,
  licensing:     <Package size={12}/>,
  system:        <Settings size={12}/>,
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface NotificationCenterProps {
  userId?: string;
  onNavigate?: (r: Route) => void;
}

export function R2NotificationCenter({ userId, onNavigate }: NotificationCenterProps): JSX.Element {
  const [open, setOpen]           = useState(false);
  const [notifications, setNotifs] = useState<any[]>([]);
  const [unreadCount, setUnread]  = useState(0);
  const [loading, setLoading]     = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch]       = useState('');
  const [marking, setMarking]     = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const loadStats = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/r2-notifications/stats`);
      const d = await r.json();
      setUnread(d.unread ?? 0);
    } catch { /* ignore */ }
  }, []);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '30' });
      if (activeCategory !== 'all') params.set('category', activeCategory);
      const r = await fetch(`${API_URL}/api/r2-notifications/?${params}`);
      const d = await r.json();
      setNotifs(d.notifications ?? []);
      setUnread(d.unread_count ?? 0);
    } finally { setLoading(false); }
  }, [activeCategory]);

  useEffect(() => { void loadStats(); const t = setInterval(() => void loadStats(), 30000); return () => clearInterval(t); }, [loadStats]);
  useEffect(() => { if (open) void loadNotifications(); }, [open, loadNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const markRead = async (id: string) => {
    await fetch(`${API_URL}/api/r2-notifications/read`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notification_id: id }),
    });
    setNotifs(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    setMarking(true);
    try {
      await fetch(`${API_URL}/api/r2-notifications/read-all`, { method: 'PUT' });
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } finally { setMarking(false); }
  };

  const deleteNotif = async (id: string) => {
    await fetch(`${API_URL}/api/r2-notifications/${id}`, { method: 'DELETE' });
    setNotifs(prev => prev.filter(n => n.notification_id !== id));
  };

  const filtered = notifications.filter(n => {
    if (search && !n.title?.toLowerCase().includes(search.toLowerCase()) && !n.message?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div ref={panelRef} style={{ position: 'relative' }} data-testid="r2-notification-center">
      {/* Bell Button */}
      <button
        data-testid="notifications-btn"
        onClick={() => setOpen(v => !v)}
        style={{
          position: 'relative', width: 32, height: 32, borderRadius: 10, background: open ? `${C.teal}15` : 'transparent',
          border: `1px solid ${open ? C.teal + '40' : 'rgba(71,85,105,0.6)'}`,
          color: open ? C.teal : 'rgba(148,163,184,0.9)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
        }}
        title="Notifications"
      >
        <Bell style={{ width: 14, height: 14 }} strokeWidth={1.75}/>
        {unreadCount > 0 && (
          <span data-testid="notification-badge" style={{
            position: 'absolute', top: -4, right: -4,
            minWidth: 16, height: 16, borderRadius: 8, padding: '0 4px',
            background: C.red, color: '#fff', fontSize: 9, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 0 8px ${C.red}60`,
            animation: 'notif-pulse 2s ease-in-out infinite',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div
          data-testid="notification-panel"
          style={{
            position: 'absolute', top: 'calc(100% + 10px)', right: 0, zIndex: 99900,
            width: 400, maxHeight: '75vh',
            background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14,
            boxShadow: '0 12px 48px rgba(0,0,0,0.55)', backdropFilter: 'blur(20px)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{ padding: '14px 16px 10px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: C.text }}>
                Notifications {unreadCount > 0 && <span style={{ fontSize: 11, color: C.red, marginLeft: 4 }}>{unreadCount} new</span>}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {unreadCount > 0 && (
                  <button
                    data-testid="mark-all-read-btn"
                    onClick={() => void markAllRead()}
                    disabled={marking}
                    style={{ background: `${C.teal}12`, border: `1px solid ${C.teal}25`, color: C.teal, borderRadius: 7, padding: '4px 10px', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    {marking ? <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }}/> : <CheckCheck size={10}/>}
                    All Read
                  </button>
                )}
                <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', padding: 2 }}><X size={14}/></button>
              </div>
            </div>

            {/* Search */}
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <Search size={11} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: C.dim }}/>
              <input
                data-testid="notification-search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search notifications…"
                style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 10px 7px 26px', color: C.text, fontSize: 11, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {/* Category tabs */}
            <div style={{ display: 'flex', gap: 4, overflowX: 'auto', paddingBottom: 2 }}>
              {CATEGORIES.map(cat => (
                <button
                  key={cat.key}
                  data-testid={`notif-cat-${cat.key}`}
                  onClick={() => setActiveCategory(cat.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 7, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, fontSize: 10, fontWeight: 600,
                    background: activeCategory === cat.key ? `${cat.color}18` : 'transparent',
                    color: activeCategory === cat.key ? cat.color : C.dim,
                    transition: 'all 0.12s',
                  }}
                >
                  {cat.icon} {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notification List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 24, textAlign: 'center' }}><Loader2 size={18} style={{ animation: 'spin 1s linear infinite', color: C.teal }}/></div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 28, textAlign: 'center', color: C.dim }}>
                <Inbox size={28} style={{ margin: '0 auto 8px', display: 'block', opacity: 0.25 }}/>
                <div style={{ fontSize: 12 }}>No notifications</div>
              </div>
            ) : filtered.map(n => {
              const pc = PRIORITY_COLOR[n.priority] ?? C.muted;
              return (
                <div
                  key={n.notification_id}
                  data-testid={`notif-item-${n.notification_id}`}
                  style={{
                    padding: '12px 14px', borderBottom: `1px solid ${C.border}`,
                    background: n.read ? 'transparent' : 'rgba(37,181,168,0.04)',
                    display: 'flex', gap: 10, transition: 'background 0.15s',
                  }}
                >
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: `${pc}12`, border: `1px solid ${pc}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: pc, flexShrink: 0 }}>
                    {CATEGORY_ICON[n.category] ?? <Bell size={12}/>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, marginBottom: 2 }}>
                      <div style={{ fontSize: 12, fontWeight: n.read ? 500 : 700, color: n.read ? C.muted : C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {n.title}
                      </div>
                      {!n.read && <span data-testid={`unread-dot-${n.notification_id}`} style={{ width: 6, height: 6, borderRadius: '50%', background: C.teal, flexShrink: 0, marginTop: 3 }}/>}
                    </div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 4, lineHeight: 1.4 }}>{n.message}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 9, color: C.dim }}>{n.created_at ? timeAgo(n.created_at) : '–'}</span>
                      <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: `${pc}12`, color: pc, fontWeight: 700, textTransform: 'uppercase' }}>{n.priority}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flexShrink: 0 }}>
                    {!n.read && (
                      <button
                        data-testid={`mark-read-btn-${n.notification_id}`}
                        onClick={() => void markRead(n.notification_id)}
                        title="Mark read"
                        style={{ background: 'none', border: 'none', color: C.teal, cursor: 'pointer', padding: 3 }}
                      >
                        <Check size={12}/>
                      </button>
                    )}
                    <button
                      data-testid={`delete-notif-btn-${n.notification_id}`}
                      onClick={() => void deleteNotif(n.notification_id)}
                      title="Delete"
                      style={{ background: 'none', border: 'none', color: C.dim, cursor: 'pointer', padding: 3 }}
                      onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = C.red}
                      onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = C.dim}
                    >
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ padding: '10px 14px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'center' }}>
            <button
              data-testid="view-all-notifications-btn"
              onClick={() => { setOpen(false); onNavigate?.('r2-marketplace'); }}
              style={{ background: 'none', border: 'none', color: C.teal, fontSize: 11, cursor: 'pointer', opacity: 0.7 }}
            >
              View all notifications
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes notif-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.8;transform:scale(1.08)}}
      `}</style>
    </div>
  );
}
